/**
 * Berndorf — Store-Locator
 *
 * Karte + durchsuchbare Filialliste für 69 Verkaufsstellen. Kein App-Abo,
 * kein Server, keine Middleware: die Daten werden in Shopify-Metaobjekten gepflegt,
 * die Karte ist Leaflet aus dem Theme.
 *
 * Ladeordnung (bewusst dreistufig, spart auf Startseite und PDP ~190 KB,
 * solange niemand die Karte öffnet):
 *
 *   1. Sichtbar (IntersectionObserver) -> Filialdaten holen, Liste rendern.
 *      Suche funktioniert ab hier — ohne Leaflet, ohne Consent, ohne Karte.
 *   2. Klick auf „Karte laden" -> Leaflet + Cluster-Plugin nachladen.
 *   3. Erst dann gehen Tile-Requests an den Kartenanbieter.
 *
 * Die Liste ist nicht Beiwerk, sondern der barrierefreie Pfad: eine
 * Leaflet-Karte ist per Tastatur/Screenreader nicht sinnvoll bedienbar.
 * Alles, was die Karte kann, muss die Liste auch können.
 *
 * 🔄 Datenquelle wechseln (z. B. auf Metaobjekte): nur `loadStores()` unten
 *    anfassen. Der Rest des Moduls kennt ausschließlich das Array-Format
 *    {id,name,street,zip,city,country,phone,email,web,type,lat,lng}.
 */
(() => {
  'use strict';

  const SELECTOR = '[data-berndorf-store-locator]';

  /** Kartenausschnitt beim ersten Laden, falls fitBounds nicht greift (AT/DE). */
  const FALLBACK_CENTER = [47.8, 13.5];
  const FALLBACK_ZOOM = 6;

  /**
   * Land, auf das der Startausschnitt einpasst. Design-Vorgabe, keine
   * technische Schranke — Kacheln gibt es seit 31.08.2026 für AT **und** DE.
   *
   * Warum überhaupt eingeschränkt: ein fitBounds über alle 69 Filialen reicht
   * bis Berlin und spannt 46,6°–52,5° Breite. Österreich, wo 67 der 69
   * Verkaufsstellen und beide Boutiquen liegen, wird darin klein und die Karte
   * öffnet auf halb Mitteleuropa. Der Heimatmarkt soll den Rahmen füllen.
   *
   * Die zwei deutschen Händler verschwinden dadurch nicht: sie stehen in der
   * Liste, tragen ihren Pin, haben dank basemap.de echten Kartenhintergrund,
   * und wer nach „Berlin" oder „Dingelstädt" sucht, filtert die AT-Sätze weg
   * und bekommt sie ganz normal eingepasst. Ebenso beim Verschieben nach
   * Norden — dort ist jetzt Karte, keine leere Fläche.
   *
   * 🔄 Auf `null` setzen, wenn der Startausschnitt wieder alle Filialen zeigen
   * soll. Beide Zustände funktionieren; das ist eine Design-, keine
   * Technikfrage. Historie: bis 31.08.2026 war die Einschränkung erzwungen,
   * weil 63 % des großen Ausschnitts unbekachelt waren — dieser Grund ist mit
   * dem zweiten Tile-Layer weggefallen, die Design-Vorgabe bleibt.
   */
  const START_FOCUS_COUNTRY = 'AT';

  /**
   * Umlaute falten — bei österreichischen Orts- und Firmennamen keine Kür.
   *
   * Es gibt zwei gebräuchliche Ersatzschreibweisen, und Nutzer tippen beide:
   * „Modling" und „Moedling", „Kruger" und „Krueger". Deshalb werden
   * Datensatz und Suchbegriff in BEIDEN Varianten verglichen (siehe
   * `matchesTerm`) — eine allein lässt jeweils die andere Hälfte ins Leere
   * laufen.
   *
   * @param {string} value
   * @param {boolean} expand  true: ü -> ue (lang), false: ü -> u (kurz)
   */
  const normalize = (value, expand) =>
    (value || '')
      .toString()
      .toLowerCase()
      .replace(/ä/g, expand ? 'ae' : 'a')
      .replace(/ö/g, expand ? 'oe' : 'o')
      .replace(/ü/g, expand ? 'ue' : 'u')
      .replace(/ß/g, 'ss')
      // Restliche Diakritika (é, č, …) abräumen — die Händlerliste enthält
      // auch nicht-deutsche Eigennamen.
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  /**
   * Ein Begriff passt, wenn er in der kurzen ODER der langen Faltung
   * vorkommt. Damit finden \u201ekruger", \u201ekrueger" und \u201ekr\u00fcger" denselben H\u00e4ndler.
   */
  const matchesTerm = (index, term) =>
    index.short.includes(normalize(term, false)) ||
    index.long.includes(normalize(term, true));

  /**
   * Suchindex je Filiale, einmal beim Laden gebaut statt bei jedem
   * Tastendruck neu \u2014 durchsucht werden Name, Stra\u00dfe, PLZ und Ort, also
   * genau die vier Felder, die die Einleitung im Design verspricht.
   */
  const buildIndex = (store) => {
    const raw = `${store.name} ${store.street} ${store.zip} ${store.city}`;
    return { short: normalize(raw, false), long: normalize(raw, true) };
  };

  const escapeHtml = (value) =>
    (value || '')
      .toString()
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  /** Telefonnummer für tel: — Leerzeichen und Klammern raus, + behalten. */
  const telHref = (phone) => phone.replace(/[^\d+]/g, '');

  /** Externe Skripte/Styles einmalig laden, mehrfacher Aufruf teilt das Promise. */
  const assetCache = new Map();

  const loadScript = (src) => {
    if (assetCache.has(src)) return assetCache.get(src);
    const promise = new Promise((resolve, reject) => {
      const el = document.createElement('script');
      el.src = src;
      el.onload = resolve;
      el.onerror = () => reject(new Error(`Konnte ${src} nicht laden`));
      document.head.appendChild(el);
    });
    assetCache.set(src, promise);
    return promise;
  };

  const loadStyle = (href) => {
    if (assetCache.has(href)) return assetCache.get(href);
    const promise = new Promise((resolve, reject) => {
      const el = document.createElement('link');
      el.rel = 'stylesheet';
      el.href = href;
      el.onload = resolve;
      el.onerror = () => reject(new Error(`Konnte ${href} nicht laden`));
      document.head.appendChild(el);
    });
    assetCache.set(href, promise);
    return promise;
  };

  class StoreLocator {
    constructor(root) {
      this.root = root;
      this.config = root.dataset;

      this.listEl = root.querySelector('[data-locator-list]');
      this.countEl = root.querySelector('[data-locator-count]');
      this.moreEl = root.querySelector('[data-locator-more]');
      this.mobilePageSize = Number(root.dataset.mobileListLimit) || 0;
      this.mobileLimit = this.mobilePageSize;
      this.mobileMedia = window.matchMedia('(max-width: 749px)');
      if (this.mobilePageSize) {
        const onResize = () => {
          this.mobileLimit = this.mobilePageSize;
          if (this.stores.length) this.renderList();
        };
        this.mobileMedia.addEventListener('change', onResize);
        const onUnload = (event) => {
          if (!event.target.contains(root)) return;
          this.mobileMedia.removeEventListener('change', onResize);
          document.removeEventListener('shopify:section:unload', onUnload);
        };
        document.addEventListener('shopify:section:unload', onUnload);
        this.moreEl?.addEventListener('click', () => {
          const previousCount = this.mobileLimit;
          this.mobileLimit += this.mobilePageSize;
          this.renderList();
          this.listEl.children[previousCount]?.focus({ preventScroll: true });
        });
      }
      this.searchEl = root.querySelector('[data-locator-search]');
      this.clearEl = root.querySelector('[data-locator-search-clear]');
      this.mapEl = root.querySelector('[data-locator-map]');
      this.consentEl = root.querySelector('[data-locator-consent]');
      this.zoomEl = root.querySelector('[data-locator-zoom]');

      this.stores = [];
      this.visible = [];
      this.markers = new Map();
      this.selectedId = null;
      this.typeFilter = 'all';
      this.map = null;
      this.cluster = null;
      this.mapLoading = false;

      this.bindEvents();
      this.observe();
    }

    /* ---------------------------------------------------------------- Daten */

    /**
     * Same-origin Liquid endpoint, paginated explicitly. Follow every page;
     * unpublished metaobjects are excluded by Shopify. No storefront token.
     */
    async loadStores() {
      const stores = [];
      const visited = new Set();
      let url = new URL(this.config.storesUrl, window.location.origin);
      while (url) {
        if (url.origin !== window.location.origin || visited.has(url.href)) {
          throw new Error('Ungültige Filialdaten-Paginierung');
        }
        visited.add(url.href);
        const response = await fetch(url.href);
        if (!response.ok) throw new Error(`Filialdaten: HTTP ${response.status}`);
        const data = await response.json();
        if (!Array.isArray(data.stores)) throw new Error('Filialdaten sind kein Array');
        stores.push(...data.stores);
        url = data.next ? new URL(data.next, url) : null;
      }
      return stores.map((store) => ({ ...store, lat: Number(store.lat), lng: Number(store.lng) }))
        .sort((a, b) => (a.type !== 'boutique') - (b.type !== 'boutique')
          || a.country.localeCompare(b.country)
          || a.zip.localeCompare(b.zip)
          || a.name.localeCompare(b.name, 'de'));
    }

    observe() {
      // Die Section steht auf der Startseite und auf jeder PDP weit unten —
      // erst laden, wenn sie in die Nähe des Viewports kommt.
      const observer = new IntersectionObserver(
        (entries, obs) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          obs.disconnect();
          this.init();
        },
        { rootMargin: '400px' }
      );
      observer.observe(this.root);
    }

    async init() {
      try {
        this.stores = await this.loadStores();
      } catch (error) {
        console.error('[berndorf-store-locator]', error);
        this.renderError();
        return;
      }

      // Suchindex einmalig anhängen (siehe buildIndex).
      this.stores.forEach((store) => {
        store._index = buildIndex(store);
      });

      if (this.config.showTypeFilter === 'true') this.renderTypeFilter();
      // Preserve a search already entered while the paginated data was loading.
      this.applyFilter();

      // Consent-Platzhalter erst jetzt zeigen: ohne JS gäbe es keine Karte,
      // dann wäre der Knopf eine Attrappe.
      if (this.consentEl) this.consentEl.hidden = false;
    }

    /* --------------------------------------------------------------- Events */

    bindEvents() {
      if (this.searchEl) {
        this.searchEl.addEventListener('input', () => {
          this.applyFilter();
        });
        // Enter darf das umgebende Formular (PDP!) nicht abschicken.
        this.searchEl.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') event.preventDefault();
        });
      }

      if (this.clearEl) {
        this.clearEl.addEventListener('click', () => {
          this.searchEl.value = '';
          this.applyFilter();
          this.searchEl.focus();
        });
      }

      const acceptEl = this.root.querySelector('[data-locator-consent-accept]');
      if (acceptEl) {
        acceptEl.addEventListener('click', () => this.loadMap());
      }

      const zoomIn = this.root.querySelector('[data-locator-zoom-in]');
      const zoomOut = this.root.querySelector('[data-locator-zoom-out]');
      if (zoomIn) zoomIn.addEventListener('click', () => this.map && this.map.zoomIn());
      if (zoomOut) zoomOut.addEventListener('click', () => this.map && this.map.zoomOut());

      // Delegation: die Liste wird bei jedem Filterlauf neu gebaut.
      if (this.listEl) {
        this.listEl.addEventListener('click', (event) => {
          const item = event.target.closest('[data-store-id]');
          // Links in der Karte (Telefon, E-Mail, Website) nicht abfangen.
          if (!item || event.target.closest('a')) return;
          this.select(item.dataset.storeId, { pan: true });
        });

        this.listEl.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          const item = event.target.closest('[data-store-id]');
          if (!item || event.target.closest('a')) return;
          event.preventDefault();
          this.select(item.dataset.storeId, { pan: true });
        });
      }
    }

    /* --------------------------------------------------------------- Filter */

    /**
     * Rein clientseitige Suche gegen die eigenen 69 Datensätze — kein
     * Geocoding-Dienst nötig, weil Name, Straße, PLZ und Ort alle im
     * Datensatz stehen.
     */
    applyFilter() {
      this.mobileLimit = this.mobilePageSize;
      const query = (this.searchEl ? this.searchEl.value : '').trim();
      const terms = query.split(/\s+/).filter(Boolean);

      this.visible = this.stores.filter((store) => {
        if (this.typeFilter !== 'all' && store.type !== this.typeFilter) return false;
        if (!terms.length) return true;
        // Alle Begriffe müssen vorkommen: „wien wollzeile" soll enger
        // treffen als „wien", nicht breiter.
        return terms.every((term) => matchesTerm(store._index, term));
      });

      if (this.clearEl) this.clearEl.hidden = !query;

      this.renderList();
      this.syncMarkers();
    }

    renderTypeFilter() {
      const options = [
        { value: 'all', label: 'Alle' },
        { value: 'boutique', label: 'Berndorf Boutique' },
        { value: 'dealer', label: 'Fachhändler' },
      ];

      const nav = document.createElement('div');
      nav.className = 'berndorf-store-locator__filter';
      nav.setAttribute('role', 'group');
      nav.setAttribute('aria-label', 'Art der Verkaufsstelle');

      options.forEach((option) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'berndorf-store-locator__filter-button';
        button.textContent = option.label;
        button.setAttribute('aria-pressed', String(option.value === this.typeFilter));
        button.addEventListener('click', () => {
          this.typeFilter = option.value;
          nav.querySelectorAll('button').forEach((el) => {
            el.setAttribute('aria-pressed', String(el === button));
          });
          this.applyFilter();
        });
        nav.appendChild(button);
      });

      this.listEl.parentNode.insertBefore(nav, this.countEl);
    }

    /* -------------------------------------------------------------- Rendern */

    renderList() {
      const total = this.stores.length;
      const shown = this.visible.length;
      const limited = this.mobilePageSize > 0 && this.mobileMedia.matches;
      const entries = limited ? this.visible.slice(0, this.mobileLimit) : this.visible;
      if (this.moreEl) this.moreEl.hidden = entries.length >= shown;

      if (this.countEl) {
        this.countEl.textContent =
          entries.length < shown
            ? this.root.dataset.locationsShown.replace('__SHOWN__', entries.length).replace('__TOTAL__', shown)
            : shown === total ? this.root.dataset.locationsTotal.replace('__TOTAL__', total) : this.root.dataset.locationsFiltered.replace('__SHOWN__', shown).replace('__TOTAL__', total);
      }

      if (!shown) {
        this.listEl.innerHTML =
          `<li class="berndorf-store-locator__empty">${escapeHtml(this.root.dataset.locationsEmpty)}</li>`;
        return;
      }

      this.listEl.innerHTML = entries.map((store) => this.storeItem(store)).join('');
    }

    storeItem(store) {
      const isBoutique = store.type === 'boutique';
      const contact = [];

      if (store.phone) {
        contact.push(
          `<a class="berndorf-store-locator__link" href="tel:${escapeHtml(telHref(store.phone))}">${escapeHtml(store.phone)}</a>`
        );
      }
      if (store.email) {
        contact.push(
          `<a class="berndorf-store-locator__link" href="mailto:${escapeHtml(store.email)}">${escapeHtml(store.email)}</a>`
        );
      }
      if (store.web) {
        contact.push(
          `<a class="berndorf-store-locator__link" href="${escapeHtml(store.web)}" target="_blank" rel="noopener noreferrer">Website</a>`
        );
      }

      return `
        <li class="berndorf-store-locator__item${isBoutique ? ' berndorf-store-locator__item--boutique' : ''}"
            data-store-id="${escapeHtml(store.id)}"
            tabindex="0"
            ${this.selectedId === store.id ? 'aria-current="true"' : ''}>
          ${isBoutique ? '<span class="berndorf-store-locator__badge">Berndorf Boutique</span>' : ''}
          <p class="berndorf-store-locator__name">${escapeHtml(store.name)}</p>
          <p class="berndorf-store-locator__address">
            ${escapeHtml(store.street)}<br>
            ${escapeHtml(store.zip)} ${escapeHtml(store.city)}
          </p>
          ${contact.length ? `<p class="berndorf-store-locator__contact">${contact.join('<span aria-hidden="true"> · </span>')}</p>` : ''}
        </li>
      `;
    }

    renderError() {
      // Die Filialliste ist die Kernfunktion — wenn sie fehlt, muss der
      // Nutzer wenigstens einen Weg zum Kontakt bekommen.
      this.listEl.innerHTML =
        `<li class="berndorf-store-locator__empty">${escapeHtml(this.root.dataset.locationsError)}</li>`;
      if (this.consentEl) this.consentEl.hidden = true;
    }

    /* ----------------------------------------------------------------- Karte */

    async loadMap() {
      if (this.map || this.mapLoading) return;
      this.mapLoading = true;

      const consentButton = this.root.querySelector('[data-locator-consent-accept]');
      if (consentButton) {
        consentButton.disabled = true;
        consentButton.textContent = this.root.dataset.mapLoading;
      }

      try {
        await Promise.all([
          loadStyle(this.config.leafletCss),
          loadStyle(this.config.clusterCss),
        ]);
        await loadScript(this.config.leafletJs);
        // Das Cluster-Plugin erweitert das globale L — muss nach Leaflet laufen.
        await loadScript(this.config.clusterJs);
      } catch (error) {
        console.error('[berndorf-store-locator]', error);
        this.mapLoading = false;
        if (consentButton) {
          consentButton.disabled = false;
          consentButton.textContent = this.root.dataset.mapError;
        }
        return;
      }

      this.initMap();
      this.mapLoading = false;
      if (this.consentEl) this.consentEl.hidden = true;
      if (this.zoomEl) this.zoomEl.hidden = false;
    }

    initMap() {
      const L = window.L;

      this.map = L.map(this.mapEl, {
        // Eigene Zoom-Knöpfe links oben (Design) statt Leaflets Default.
        zoomControl: false,
        // Scroll-Zoom greift sonst beim Vorbeiscrollen die Seite ab — auf
        // Startseite und PDP steht die Karte mitten im Lesefluss.
        scrollWheelZoom: false,
        center: FALLBACK_CENTER,
        zoom: FALLBACK_ZOOM,
      });

      // Nach einem Klick in die Karte ist Scroll-Zoom erwünscht.
      this.map.on('click', () => this.map.scrollWheelZoom.enable());
      this.map.on('mouseout', () => this.map.scrollWheelZoom.disable());

      const maxZoom = Number(this.config.tileMaxZoom) || 18;

      // Zwei Grundkarten übereinander, eine je Land — siehe Kopfkommentar der
      // Section. Das funktioniert ohne Umschaltlogik, weil beide Dienste
      // außerhalb ihres Gebiets nichts Deckendes liefern: basemap.de gibt eine
      // vollständig transparente Kachel zurück (RGBA, Alpha 0 — am 31.08.2026
      // nachgemessen), basemap.at antwortet dort mit 404 und Leaflet lässt die
      // Stelle leer. Wo der eine schweigt, malt der andere.
      //
      // Reihenfolge: Ergänzungsgebiet zuerst, Hauptkarte darüber. Österreich
      // ist der Hauptmarkt; bei einer Überlappung soll die AT-Kartografie
      // gewinnen.
      if (this.config.tileUrlSecondary) {
        L.tileLayer(this.config.tileUrlSecondary, {
          maxZoom,
          attribution: this.config.tileAttributionSecondary,
        }).addTo(this.map);
      }

      L.tileLayer(this.config.tileUrl, {
        maxZoom,
        attribution: this.config.tileAttribution,
      }).addTo(this.map);

      this.cluster = L.markerClusterGroup({
        maxClusterRadius: 45,
        showCoverageOnHover: false,
        iconCreateFunction: (cluster) =>
          L.divIcon({
            className: 'berndorf-locator-cluster',
            html: `<span>${cluster.getChildCount()}</span>`,
            iconSize: [34, 34],
          }),
      });

      this.stores.forEach((store) => {
        const marker = L.marker([store.lat, store.lng], {
          icon: this.pinIcon(store, false),
          // Marker aus der Tab-Reihenfolge nehmen: die Liste ist der
          // barrierefreie Pfad, 69 unbeschriftete Pins im Fokuslauf wären
          // eine Tastaturfalle.
          keyboard: false,
          title: store.name,
        });
        marker.on('click', () => this.select(store.id, { pan: false }));
        this.markers.set(store.id, marker);
      });

      this.map.addLayer(this.cluster);
      this.syncMarkers();
    }

    /**
     * Pin als divIcon statt Bilddatei: die Zustände (normal/aktiv) sind damit
     * reines CSS und tragen die Design-Tokens — kein zweites PNG nötig.
     */
    pinIcon(store, active) {
      return window.L.divIcon({
        className: [
          'berndorf-locator-pin',
          store.type === 'boutique' ? 'berndorf-locator-pin--boutique' : '',
          active ? 'berndorf-locator-pin--active' : '',
        ]
          .filter(Boolean)
          .join(' '),
        html: '<span></span>',
        iconSize: [14, 14],
        iconAnchor: [7, 7],
      });
    }

    /** Nur die gefilterten Filialen auf der Karte lassen. */
    syncMarkers() {
      if (!this.map || !this.cluster) return;

      this.cluster.clearLayers();
      const layers = this.visible
        .map((store) => this.markers.get(store.id))
        .filter(Boolean);
      this.cluster.addLayers(layers);

      if (!layers.length) return;

      // Siehe START_FOCUS_COUNTRY: einpassen auf den Heimatmarkt, und nur
      // solange davon überhaupt eine Filiale sichtbar ist. Sonst (Suche nach
      // „Berlin") auf alles Sichtbare — dann ist der DE-Händler ja gemeint.
      const home = START_FOCUS_COUNTRY
        ? this.visible.filter((store) => store.country === START_FOCUS_COUNTRY)
        : this.visible;
      const focus = home.length ? home : this.visible;

      const bounds = window.L.latLngBounds(
        focus.map((store) => [store.lat, store.lng])
      );
      if (bounds.isValid()) {
        this.map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
      }
    }

    /* -------------------------------------------------------------- Auswahl */

    /**
     * Karte und Liste sind zwei Ansichten derselben Daten und in beide
     * Richtungen gekoppelt: Pin-Klick markiert die Zeile, Zeilen-Klick
     * bewegt die Karte.
     */
    select(storeId, { pan }) {
      const store = this.stores.find((entry) => entry.id === storeId);
      if (!store) return;

      const previousId = this.selectedId;
      this.selectedId = storeId;
      // A map pin must still reveal its corresponding row in a paged mobile list.
      const index = this.visible.findIndex((entry) => entry.id === storeId);
      if (this.mobilePageSize && this.mobileMedia.matches && index >= this.mobileLimit) {
        this.mobileLimit = index + 1;
        this.renderList();
      }

      if (this.map) {
        if (previousId && this.markers.has(previousId)) {
          const previousStore = this.stores.find((entry) => entry.id === previousId);
          this.markers.get(previousId).setIcon(this.pinIcon(previousStore, false));
        }

        const marker = this.markers.get(storeId);
        if (marker) {
          marker.setIcon(this.pinIcon(store, true));
          marker.bindPopup(this.popupHtml(store), {
            className: 'berndorf-locator-popup',
            closeButton: true,
          });
          if (pan) {
            // zoomToShowLayer klappt das Cluster auf, in dem der Pin steckt —
            // ohne das würde ein Klick in der Liste scheinbar nichts tun.
            this.cluster.zoomToShowLayer(marker, () => marker.openPopup());
          } else {
            marker.openPopup();
          }
        }
      }

      this.listEl.querySelectorAll('[data-store-id]').forEach((el) => {
        const isSelected = el.dataset.storeId === storeId;
        el.classList.toggle('berndorf-store-locator__item--selected', isSelected);
        if (isSelected) {
          el.setAttribute('aria-current', 'true');
          el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        } else {
          el.removeAttribute('aria-current');
        }
      });
    }

    popupHtml(store) {
      const rows = [`<strong>${escapeHtml(store.name)}</strong>`];
      rows.push(`${escapeHtml(store.street)}<br>${escapeHtml(store.zip)} ${escapeHtml(store.city)}`);
      if (store.phone) {
        rows.push(
          `<a href="tel:${escapeHtml(telHref(store.phone))}">${escapeHtml(store.phone)}</a>`
        );
      }
      if (store.web) {
        rows.push(
          `<a href="${escapeHtml(store.web)}" target="_blank" rel="noopener noreferrer">Website</a>`
        );
      }
      return rows.join('<br>');
    }
  }

  const init = (scope) => {
    (scope || document).querySelectorAll(SELECTOR).forEach((root) => {
      if (root.dataset.locatorReady === 'true') return;
      root.dataset.locatorReady = 'true';
      new StoreLocator(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Theme-Editor: Section wird beim Bearbeiten neu eingehängt.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
